import React from 'react'
import { Button, Card, CardContent, CardHeader, Input, Label, Table, Thead, Tbody, Tr, Th, Td, Badge } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { supabase } from '@/lib/supabase'
import { getOwnerContactsByAccountIds } from '@/modules/accounts/services/ownerContactService'
import { listActiveRewardsTrialAttempts, setRewardsTrialOverride } from '../../services/rewardsAdminService'
import type { RewardsTrialAttempt } from '../../types'

function formatIso(iso?: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('pt-BR')
  } catch {
    return iso
  }
}

function statusBadge(st: RewardsTrialAttempt['status']) {
  const base = { variant: 'soft' as const }
  if (st === 'approved') return <Badge {...base} color="success">approved</Badge>
  if (st === 'failed') return <Badge {...base} color="error">failed</Badge>
  if (st === 'active') return <Badge {...base} color="warning">active</Badge>
  return <Badge {...base} color="secondary">{st}</Badge>
}

export default function RewardsSettingsTrialCard() {
  const { add: toast } = useToast()
  const [loading, setLoading] = React.useState(true)
  const [running, setRunning] = React.useState(false)
  const [savingId, setSavingId] = React.useState<string | null>(null)
  const [rows, setRows] = React.useState<RewardsTrialAttempt[]>([])
  const [ownerByAccount, setOwnerByAccount] = React.useState<Map<string, any>>(new Map())
  const [error, setError] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      const data = await listActiveRewardsTrialAttempts()
      setRows(data)
      const accountIds = Array.from(new Set(data.map((r) => r.account_id)))
      const owners = await getOwnerContactsByAccountIds(accountIds)
      setOwnerByAccount(owners as any)
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar trials')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  async function runGuard() {
    setRunning(true)
    try {
      const { data, error } = await supabase.functions.invoke('rewards-trial-guard-runner', { body: { limit: 100 } as any })
      if (error) throw error
      const payload = Array.isArray(data) ? (data as any)[0] : (data as any)
      toast({ title: 'Rewards - Trial guard', description: `Processados: ${payload?.processed ?? 0}`, variant: 'success', soft: true })
      await load()
    } catch (e: any) {
      toast({ title: 'Rewards - Trial guard', description: e?.message || 'Falha ao executar', variant: 'error' })
    } finally {
      setRunning(false)
    }
  }

  async function saveOverride(row: RewardsTrialAttempt, passed: boolean, volume: number, note: string) {
    setSavingId(row.id)
    try {
      const { data } = await supabase.auth.getUser()
      const uid = (data as any)?.user?.id || null
      await setRewardsTrialOverride({
        id: row.id,
        overridePassed: passed,
        overrideVolumeUsdt: volume,
        note,
        overriddenBy: uid,
      })
      toast({ title: 'Rewards - Trial', description: 'Override salvo.', variant: 'success', soft: true })
      await load()
    } catch (e: any) {
      toast({ title: 'Rewards - Trial', description: e?.message || 'Falha ao salvar override', variant: 'error' })
    } finally {
      setSavingId(null)
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3 text-base font-semibold">
        <span>Rewards - Trial (MASTER)</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={loading || running || !!savingId}>
            Recarregar
          </Button>
          <Button onClick={runGuard} disabled={running || loading || !!savingId}>
            {running ? 'Executando...' : 'Rodar guard agora'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-foreground/70">
          O guard avalia tentativas vencidas. Sem API automatizada, use <span className="font-medium text-foreground">override</span> para aprovar/reprovar manualmente antes do prazo.
        </p>
        {error && <div className="rounded-md border border-red-500/30 bg-red-500/10 p-2 text-sm text-foreground">{error}</div>}

        {loading ? (
          <div className="text-sm text-foreground/70">Carregando...</div>
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-sm text-foreground/70">Sem trials.</div>
        ) : (
          <div className="overflow-auto rounded-md border border-border/60">
            <Table>
              <Thead>
                <Tr>
                  <Th>Cliente</Th>
                  <Th>Status</Th>
                  <Th>Deadline</Th>
                  <Th>Min (USDT)</Th>
                  <Th>Override</Th>
                  <Th>Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((r) => {
                  const owner = (ownerByAccount as any).get(r.account_id) || null
                  const label = owner?.email || owner?.name || r.user_id
                  const disabled = savingId === r.id
                  return (
                    <TrialRow
                      key={r.id}
                      row={r}
                      label={label}
                      disabled={disabled}
                      onSave={(p) => saveOverride(r, p.passed, p.volume, p.note)}
                    />
                  )
                })}
              </Tbody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )

  function TrialRow({
    row,
    label,
    disabled,
    onSave,
  }: {
    row: RewardsTrialAttempt
    label: string
    disabled: boolean
    onSave: (p: { passed: boolean; volume: number; note: string }) => Promise<void>
  }) {
    const [volume, setVolume] = React.useState<number>(row.override_volume_usdt ?? row.volume_usdt_at_deadline ?? 0)
    const [passed, setPassed] = React.useState<boolean>(row.override_passed ?? false)
    const [note, setNote] = React.useState<string>(row.override_note || '')

    return (
      <Tr>
        <Td>
          <div className="min-w-[240px]">
            <div className="text-sm font-medium">{label}</div>
            <div className="text-xs text-foreground/60">{row.account_id}</div>
          </div>
        </Td>
        <Td>{statusBadge(row.status)}</Td>
        <Td className="text-xs">{formatIso(row.check_deadline_at)}</Td>
        <Td className="font-mono text-xs">{Number(row.min_volume_usdt || 0).toLocaleString('pt-BR')}</Td>
        <Td>
          <div className="grid min-w-[260px] gap-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Volume</Label>
                <Input type="number" value={volume} onChange={(e) => setVolume(Number((e.target as HTMLInputElement).value))} disabled={disabled} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Passou?</Label>
                <select
                  className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-ring"
                  value={passed ? '1' : '0'}
                  onChange={(e) => setPassed((e.target as HTMLSelectElement).value === '1')}
                  disabled={disabled}
                >
                  <option value="0">Não</option>
                  <option value="1">Sim</option>
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Nota (opcional)</Label>
              <Input value={note} onChange={(e) => setNote((e.target as HTMLInputElement).value)} disabled={disabled} />
            </div>
            <Button variant="outline" onClick={() => onSave({ passed, volume, note })} disabled={disabled}>
              {disabled ? 'Salvando...' : 'Salvar override'}
            </Button>
          </div>
        </Td>
        <Td className="text-xs">
          <div>Início: {formatIso(row.started_at)}</div>
          <div>Trial fim: {formatIso(row.trial_end_at)}</div>
          <div>Sub: {row.stripe_subscription_id ? row.stripe_subscription_id : '—'}</div>
        </Td>
      </Tr>
    )
  }
}

